import {
  PeopleTableInfo,
  Person,
  PersonCompanyGeneralInfo,
  PersonContactDetailsItem,
} from '@aibees/elephant/feature/search-table-people/types';
import { ROUTES as R } from '@aibees/elephant/router';
import { ENRICHMENT_STATE } from '@aibees/elephant/types';
import { EmploymentType, RoleDisplayLabel } from '@aibees/elephant/utils';
import { Nullable } from '@aibees/shared/definition';
import { fDate, fToNow } from '@aibees/shared/utils';
import { ContactDetailsSliderTabTitle } from './types';

export function doesContactDetailHaveUpdate(
  item: PersonContactDetailsItem,
  data?: PeopleTableInfo
) {
  return !!data?.changes?.CONTACT?.includes(item);
}

// On a not-yet-unlocked profile, clicking Update sends CONTACT_UPDATE_JOB_CHANGE
// which only refreshes EXPERIENCE — so non-experience updates in changes.CONTACT
// are not actionable until the profile is unlocked.
export function doesContactDetailHaveActionableUpdate(
  item: PersonContactDetailsItem,
  data?: PeopleTableInfo
) {
  if (!doesContactDetailHaveUpdate(item, data)) return false;
  const profileUnlocked = !!data?.revelation?.contact?.state;
  if (profileUnlocked) return true;
  return item === PersonContactDetailsItem.EXPERIENCE;
}

export function doesContactDetailHaveAnyUpdates(data?: PeopleTableInfo) {
  return !!data?.changes?.CONTACT?.length;
}

export function profileDetailsLatestUpdateDate(data?: PeopleTableInfo) {
  return fToNow(data?.revelation?.contact?.created);
}

export function getContactDetailsCompanyLink(
  item?: Nullable<PersonCompanyGeneralInfo>
) {
  const id = item?.id;
  return id && R.company.replace(':id', id);
}

export function formattedContactDetailsDate(date?: Nullable<string>) {
  return fDate(date, 'yyyy');
}

export function formattedStartToEndDuration(
  start?: Nullable<string>,
  end?: Nullable<string>
) {
  return `${formattedContactDetailsDate(start)} - ${
    !end ? 'Present' : formattedContactDetailsDate(end)
  }`;
}

export function contactDetailsAvailableItems(data?: PeopleTableInfo) {
  return data?.revelation?.contact?.content ?? data?.available ?? [];
}

export function contactDetailsUnlockableItems(data?: PeopleTableInfo) {
  const available = contactDetailsAvailableItems(data);
  const hasJobChange = isJobChangeHappened(data);
  return available.filter(
    (i) =>
      (i !== PersonContactDetailsItem.EXPERIENCE || hasJobChange) &&
      i !== PersonContactDetailsItem.LANGUAGES
  );
}

export function isRefetchNeededAfterRevealContactDetails(data?: Person) {
  let response = false;
  if (data?.revelation?.contact?.state === ENRICHMENT_STATE.SUCCESS) {
    data.revelation.contact.content?.map((item) => {
      switch (item) {
        case PersonContactDetailsItem.EXPERIENCE:
          if (!data?.positionGroups) response = true;
          break;
        case PersonContactDetailsItem.PUBLICATIONS:
          if (!data?.publications?.length) response = true;
          break;
        case PersonContactDetailsItem.VOLUNTEER_EXPERIENCES:
          if (!data?.volunteerExperiences?.length) response = true;
          break;
        case PersonContactDetailsItem.CERTIFICATIONS:
          if (!data?.certifications?.length) response = true;
          break;
        case PersonContactDetailsItem.PROJECTS:
          if (!data?.projects?.length) response = true;
          break;
        case PersonContactDetailsItem.EDUCATIONS:
          if (!data?.educations?.length) response = true;
          break;
        // TODO: this case will change when-ever the back-end API is ready
        // case PersonContactDetailsItem.RECOMMENDATION:
        //   return false;
        case PersonContactDetailsItem.LANGUAGES:
          if (!data?.languages?.profile_languages?.length) response = true;
          break;
        default:
          break;
      }
    });
  }
  return response;
}

export function isJobChangeHappened(data?: PeopleTableInfo) {
  if (!data?.changes?.CONTACT?.length) return false;

  if (data.changes.CONTACT.includes(PersonContactDetailsItem.EXPERIENCE))
    return true;

  return false;
}

function formatCurrentCompanyList(companies: string[]) {
  if (companies.length === 1) return companies[0];
  if (companies.length === 2) return `${companies[0]} and ${companies[1]}`;
  return `${companies[0]}, ${companies[1]}, and ${companies.length - 2} more`;
}

function formatEmploymentClause(type: NonNullable<EmploymentType>) {
  return type === 'Freelancer' ? 'a Freelancer' : 'Self-Employed';
}

export function getJobChangeAlertMessage(params: {
  firstName: Nullable<string> | undefined;
  currentLabels: RoleDisplayLabel[];
  previousLabel: RoleDisplayLabel | null;
}) {
  const name = params.firstName?.trim() || 'This person';
  const { currentLabels, previousLabel } = params;

  const currentCompanies = currentLabels
    .filter(
      (l): l is Extract<RoleDisplayLabel, { kind: 'company' }> =>
        l.kind === 'company'
    )
    .map((l) => l.name);

  if (currentCompanies.length > 0) {
    const list = formatCurrentCompanyList(currentCompanies);
    return `${name}'s work experience has been updated — currently listed at ${list}.`;
  }

  const currentEmployment = currentLabels.find(
    (l): l is Extract<RoleDisplayLabel, { kind: 'employment' }> =>
      l.kind === 'employment'
  );

  if (currentEmployment) {
    return `${name}'s work experience has been updated — currently listed as ${formatEmploymentClause(
      currentEmployment.type
    )}.`;
  }

  if (previousLabel?.kind === 'company') {
    return `${name} is no longer listed at ${previousLabel.name}.`;
  }

  if (previousLabel?.kind === 'employment') {
    return `${name} is no longer listed as ${formatEmploymentClause(
      previousLabel.type
    )}.`;
  }

  return `${name}'s work experience has been updated.`;
}

export function showProfileInsightItemTabBadge(
  locked: boolean,
  key: ContactDetailsSliderTabTitle,
  hasJobChange: boolean,
  loading: boolean,
  hasUpdate: boolean
) {
  return (
    locked ||
    (key === ContactDetailsSliderTabTitle.Work_History && hasJobChange) ||
    loading ||
    hasUpdate
  );
}
